import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createConfirmationFocusCoordinator,
  createConfirmationRequestCoordinator,
} from './confirmation-dialog-state.js';

const CONFIRMATION_TONES = new Set(['accent', 'warning', 'danger']);
const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function textOption(value, fallback) {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

// OGC-452: a destructive confirmation can carry a plain-language ledger of
// consequences (`details`: [{ label, value }]) and require the person to type
// an exact value (`confirmation`: { label, expected, hint }) before the confirm
// button enables. Both are optional; existing callers are unchanged.
const MAX_CONFIRMATION_DETAILS = 8;

export function normalizeConfirmationText(value) {
  return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').toLocaleLowerCase() : '';
}

function detailsOption(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => ({
      label: textOption(entry.label, ''),
      value: textOption(entry.value, ''),
    }))
    .filter((entry) => entry.label && entry.value)
    .slice(0, MAX_CONFIRMATION_DETAILS);
}

function typedConfirmationOption(value) {
  if (!value || typeof value !== 'object') return null;
  const expected = textOption(value.expected, '');
  if (!expected) return null;
  return Object.freeze({
    label: textOption(value.label, 'Type the name to confirm'),
    expected,
    hint: textOption(value.hint, 'Enter the name exactly as shown'),
  });
}

function confirmationOptions(options = {}) {
  return Object.freeze({
    eyebrow: textOption(options.eyebrow, 'Please confirm'),
    title: textOption(options.title, 'Confirm this action?'),
    description: textOption(options.description, 'Review the details before continuing.'),
    confirmLabel: textOption(options.confirmLabel, 'Confirm'),
    cancelLabel: textOption(options.cancelLabel, 'Cancel'),
    tone: CONFIRMATION_TONES.has(options.tone) ? options.tone : 'accent',
    details: detailsOption(options.details),
    confirmation: typedConfirmationOption(options.confirmation),
  });
}

function activeInvoker() {
  if (typeof document === 'undefined') return null;
  const element = document.activeElement;
  if (
    !element
    || element === document.body
    || element === document.documentElement
    || typeof element.focus !== 'function'
  ) return null;
  return element;
}

function focusElement(element) {
  if (!element || element.isConnected === false || typeof element.focus !== 'function') return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

export function ConfirmationDialog({
  open = false,
  requestId = 0,
  eyebrow = 'Please confirm',
  title = 'Confirm this action?',
  description = 'Review the details before continuing.',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'accent',
  details = [],
  confirmation = null,
  returnFocus = null,
  onConfirm,
  onCancel,
}) {
  const generatedId = useId().replaceAll(':', '');
  const titleId = `bl-confirmation-${generatedId}-title`;
  const descriptionId = `bl-confirmation-${generatedId}-description`;
  const typedFieldId = `bl-confirmation-${generatedId}-typed`;
  const typedHintId = `bl-confirmation-${generatedId}-typed-hint`;
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const typedRef = useRef(null);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const safeTone = CONFIRMATION_TONES.has(tone) ? tone : 'accent';
  const safeDetails = useMemo(() => detailsOption(details), [details]);
  const typed = useMemo(() => typedConfirmationOption(confirmation), [confirmation]);
  const [typedValue, setTypedValue] = useState('');
  const typedMatches = !typed
    || normalizeConfirmationText(typedValue) === normalizeConfirmationText(typed.expected);

  useEffect(() => {
    setTypedValue('');
  }, [open, requestId]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const dialog = dialogRef.current;
    const restoreTarget = returnFocus;
    // A typed confirmation opens on its field; otherwise Cancel is the safe
    // first focus.
    focusElement(typedRef.current || cancelRef.current || dialog);

    function focusableElements() {
      return [...(dialog?.querySelectorAll(FOCUSABLE_SELECTOR) || [])]
        .filter((element) => element.getAttribute('aria-hidden') !== 'true');
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCancelRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;

      const controls = focusableElements();
      if (!controls.length) {
        event.preventDefault();
        focusElement(dialog);
        return;
      }
      const first = controls[0];
      const last = controls[controls.length - 1];
      const active = document.activeElement;
      if (!dialog?.contains(active)) {
        event.preventDefault();
        focusElement(event.shiftKey ? last : first);
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        focusElement(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        focusElement(first);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      focusElement(restoreTarget);
    };
  }, [open, requestId, returnFocus]);

  if (!open) return null;

  return (
    <div className="bl-confirmation-backdrop" data-testid="confirmation-backdrop">
      <section
        ref={dialogRef}
        className={`bl-confirmation-dialog is-${safeTone}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        data-tone={safeTone}
        tabIndex={-1}
      >
        <div className="bl-confirmation-dialog-copy">
          {eyebrow ? <div className="bl-confirmation-eyebrow">{eyebrow}</div> : null}
          <h2 id={titleId}>{title}</h2>
          <p id={descriptionId}>{description}</p>
        </div>
        {safeDetails.length ? (
          <dl className="bl-confirmation-details" data-testid="confirmation-details">
            {safeDetails.map((entry) => (
              <div key={entry.label} className="bl-confirmation-detail">
                <dt>{entry.label}</dt>
                <dd>{entry.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {typed ? (
          <div className="bl-confirmation-typed">
            <label htmlFor={typedFieldId}>{typed.label}</label>
            <input
              ref={typedRef}
              id={typedFieldId}
              type="text"
              className="bl-themed-input"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              value={typedValue}
              aria-describedby={typedHintId}
              aria-invalid={typedValue && !typedMatches ? 'true' : undefined}
              onChange={(event) => setTypedValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && typedMatches) {
                  event.preventDefault();
                  onConfirm?.({ typedValue });
                }
              }}
            />
            <p id={typedHintId} className="bl-confirmation-typed-hint">
              {typedMatches ? `Matches “${typed.expected}”.` : typed.hint}
            </p>
          </div>
        ) : null}
        <div className="bl-confirmation-dialog-actions">
          <button
            ref={cancelRef}
            type="button"
            className="bl-confirmation-button is-cancel"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`bl-confirmation-button is-confirm is-${safeTone}`}
            disabled={!typedMatches}
            aria-disabled={typedMatches ? undefined : 'true'}
            onClick={typedMatches ? () => onConfirm?.(typed ? { typedValue } : undefined) : undefined}
          >
            {confirmLabel}
          </button>
        </div>
      </section>
    </div>
  );
}

export function useConfirmationDialog() {
  const [dialog, setDialog] = useState(null);
  const coordinatorRef = useRef(null);
  const focusCoordinatorRef = useRef(null);
  const mountedRef = useRef(false);
  if (!coordinatorRef.current) {
    coordinatorRef.current = createConfirmationRequestCoordinator();
  }
  if (!focusCoordinatorRef.current) {
    focusCoordinatorRef.current = createConfirmationFocusCoordinator();
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      coordinatorRef.current.cancel();
      focusCoordinatorRef.current.clear();
    };
  }, []);

  const settle = useCallback((requestId, value, detail) => {
    const coordinator = coordinatorRef.current;
    if (!coordinator.isActive(requestId)) return false;
    const settled = coordinator.settle(requestId, value, detail);
    if (!settled) return false;
    focusCoordinatorRef.current.clear();
    if (mountedRef.current) {
      setDialog((current) => current?.requestId === requestId ? null : current);
    }
    return true;
  }, []);

  const request = useCallback((options) => new Promise((resolve) => {
    const coordinator = coordinatorRef.current;
    const requestId = coordinator.replace(resolve);
    if (!mountedRef.current) {
      coordinator.settle(requestId, false);
      return;
    }
    setDialog({
      ...confirmationOptions(options),
      requestId,
      returnFocus: focusCoordinatorRef.current.capture(activeInvoker),
    });
  }), []);

  const dialogProps = useMemo(() => {
    if (!dialog) return Object.freeze({ open: false });
    return Object.freeze({
      ...dialog,
      open: true,
      onCancel: () => settle(dialog.requestId, false),
      // OGC-452: a typed confirmation resolves the request with the exact
      // text the person entered so the caller can send it for the server's
      // own check, rather than echoing the name it already knows.
      onConfirm: (detail) => settle(dialog.requestId, true, detail),
    });
  }, [dialog, settle]);

  return useMemo(() => Object.freeze({ request, dialogProps }), [dialogProps, request]);
}
